import { FC, MouseEvent, useState } from "react";

import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useGetAllNotificationsQuery, usePostMarkNotificationAsReadMutation } from "@store/services/notifications";

import { getLatestNotifications } from "./NotificationsMenu.utilities";

import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationItem from "./components/NotificationItem";

import { brandColorTokens } from "@constants";
import { applicationSpaRoutes } from "@shared/constants/routes";
import { headerActionButtonStyle, headerBadgeStyle } from "../../Header.styles";
import { headerMenuOrigins } from "../../Header.constants";

import { INotification } from "@types";

const NotificationsMenu: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const {
    data: notifications,
    isLoading: isNotificationsLoading,
    isError: isNotificationsError,
  } = useGetAllNotificationsQuery();
  const [markNotificationAsRead] = usePostMarkNotificationAsReadMutation();

  const latestNotifications = notifications ? getLatestNotifications(notifications) : [];
  const isUnreadNotification = notifications?.some((notification) => !notification.is_read) === true;
  const isMenuOpen = anchorElement !== null;
  const isEmptyStateShown = !isNotificationsLoading && !isNotificationsError && latestNotifications.length === 0;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => setAnchorElement(event.currentTarget);

  const handleMenuClose = () => setAnchorElement(null);

  const handleNotificationOpen = (notification: INotification) => {
    setAnchorElement(null);

    if (!notification.is_read) {
      markNotificationAsRead(notification.id)
        .unwrap()
        .catch(() => enqueueSnackbar(t("global.errors.genericError"), { variant: "error" }));
    }

    navigate(applicationSpaRoutes.notifications);
  };

  return (
    <>
      <IconButton
        aria-label={t("appHeader.notifications.showMyNotifications")}
        onClick={handleMenuOpen}
        data-cy="notification-button"
        data-cy-unread={isUnreadNotification}
        data-amp-default-track={true}
        data-amp-event-type="header.button.toggleNotificationPopup"
        sx={headerActionButtonStyle}
      >
        <Badge variant="dot" color="primary" invisible={!isUnreadNotification} sx={headerBadgeStyle}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={isMenuOpen}
        onClose={handleMenuClose}
        {...headerMenuOrigins}
        slotProps={{ paper: { sx: { width: 360, maxWidth: "calc(100vw - 32px)" } } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ color: brandColorTokens.text.display }}>
            {t("notifications.title")}
          </Typography>
        </Box>
        <Divider />
        {isNotificationsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isNotificationsError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{t("global.errors.genericError")}</Alert>
          </Box>
        )}
        {isEmptyStateShown && (
          <Box
            data-cy="notifications-empty-state"
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, px: 2, py: 3 }}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 32, color: brandColorTokens.icon.subtle }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("appHeader.notifications.noNotifications")}
            </Typography>
          </Box>
        )}
        {latestNotifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} onOpen={handleNotificationOpen} />
        ))}
        {latestNotifications.length > 0 && <Divider />}
        {latestNotifications.length > 0 && (
          <MenuItem
            component={RouterLink}
            to={applicationSpaRoutes.notifications}
            onClick={handleMenuClose}
            data-amp-default-track={true}
            data-amp-event-type="header.link.seeAllNotification"
            sx={{ justifyContent: "center", gap: 0.5, py: 1.5, fontWeight: 500, color: brandColorTokens.text.display }}
          >
            {t("appHeader.notifications.seeAll")}
            <ArrowForwardIcon fontSize="small" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
